'use client';

import { PrivyProvider } from '@privy-io/react-auth';
import { ReactNode } from 'react';
import {createSolanaRpc, createSolanaRpcSubscriptions} from '@solana/kit';
import {toSolanaWalletConnectors} from '@privy-io/react-auth/solana';
import {base, polygon, arbitrum, mainnet, xLayer, bsc, optimism} from 'viem/chains';
import {addRpcUrlOverrideToChain} from '@privy-io/chains';

const ethereumRpcUrl = 'https://rpc.ankr.com/eth/c6b738a490992d7e86036906e1bcc5eb313cea37a518118a3d7486242d7c1eb0';
const baseRpcUrl = 'https://rpc.ankr.com/base/c6b738a490992d7e86036906e1bcc5eb313cea37a518118a3d7486242d7c1eb0';
const polygonRpcUrl = 'https://rpc.ankr.com/polygon/c6b738a490992d7e86036906e1bcc5eb313cea37a518118a3d7486242d7c1eb0';
const arbitrumRpcUrl = 'https://rpc.ankr.com/arbitrum/c6b738a490992d7e86036906e1bcc5eb313cea37a518118a3d7486242d7c1eb0';
const xLayerRpcUrl = 'https://rpc.ankr.com/xlayer/c6b738a490992d7e86036906e1bcc5eb313cea37a518118a3d7486242d7c1eb0';
const bscRpcUrl = 'https://rpc.ankr.com/bsc/c6b738a490992d7e86036906e1bcc5eb313cea37a518118a3d7486242d7c1eb0';
const optimismRpcUrl = 'https://rpc.ankr.com/optimism/c6b738a490992d7e86036906e1bcc5eb313cea37a518118a3d7486242d7c1eb0';

const mainnetOverride = addRpcUrlOverrideToChain(mainnet, ethereumRpcUrl);
const baseOverride = addRpcUrlOverrideToChain(base, baseRpcUrl);
const polygonOverride = addRpcUrlOverrideToChain(polygon, polygonRpcUrl);
const arbitrumOverride = addRpcUrlOverrideToChain(arbitrum, arbitrumRpcUrl);
const xLayerOverride = addRpcUrlOverrideToChain(xLayer, xLayerRpcUrl);
const bscOverride = addRpcUrlOverrideToChain(bsc, bscRpcUrl);
const optimismOverride = addRpcUrlOverrideToChain(optimism, optimismRpcUrl);


/**
 * Privy 认证提供者 - 统一的用户身份认证层
 * 
 * 功能：
 * - 社交登录（Google）
 * - 嵌入式钱包自动创建
 * - 统一的用户身份管理
 */
export function PrivyAuthProvider({ children }: { children: ReactNode }) {
  return (
    <PrivyProvider
      appId={process.env.NEXT_PUBLIC_PRIVY_APP_ID!}
      config={{

        solana: {
            rpcs: {
              'solana:mainnet': {
                rpc: createSolanaRpc('https://rpc.ankr.com/solana/c6b738a490992d7e86036906e1bcc5eb313cea37a518118a3d7486242d7c1eb0'), // or your custom RPC endpoint
                rpcSubscriptions: createSolanaRpcSubscriptions('wss://rpc.ankr.com/solana/ws/c6b738a490992d7e86036906e1bcc5eb313cea37a518118a3d7486242d7c1eb0') // or your custom RPC endpoint
              }
            }
          },
        // 登录方式配置
        loginMethods: ['google', 'email', 'wallet'],
        // 高级版本：精确控制登录顺序 可以使okx在最前 但是其他钱包会显示成more options , 先不打开
        // loginMethodsAndOrder: {
        //   primary: ['okx_wallet', 'google'],  // 首屏显示：OKX + Google
        //   overflow: [  // "更多选项" 显示其他钱包
        //     'metamask',
        //     'phantom',
        //     'binance',
        //     'coinbase_wallet',
        //     'rainbow',
        //     'wallet_connect',
        //   ],
        // },
        supportedChains: [baseOverride, polygonOverride, arbitrumOverride, mainnetOverride, xLayerOverride, bscOverride, optimismOverride],
        
        // 嵌入式钱包配置
        // 为没有钱包的用户自动创建嵌入式钱包
        embeddedWallets: {
          ethereum: {
            createOnLogin: 'all-users',
          },
          solana: {
            createOnLogin: 'all-users',
          },
          // 交易广播后立即返回，不等待上链确认
          waitForTransactionConfirmation: false,
          // 全局禁用签名确认弹窗（用于 Safe 交易等静默签名场景）
          showWalletUIs: false,
        },
        externalWallets: {
            solana: {
              connectors: toSolanaWalletConnectors() // For detecting EOA browser wallets
            }
          },
        // 外观配置
        appearance: {
          theme: 'dark',              
          accentColor: '#676FFF',    
          logo: (
            <img 
              src="/favicon.svg" 
              width={48}
              height={48}
            />
          ),
          showWalletLoginFirst: false,
          walletList:  ["okx_wallet", 'metamask','phantom','binance','coinbase_wallet', 'rainbow', 'wallet_connect'],
        },
      }}
    >
      {children}
    </PrivyProvider>
  );
}

